import React, { forwardRef, useMemo } from "react";
import styles from "./Dial.module.scss";

interface Period {
  id: number;
  title: string;
}

interface DialProps {
  total: number;
  activeIndex: number;
  rotation: number;
  groupRotation: number;
  onDotClick: (index: number) => void;
  isAnimating?: boolean;
  anchorDeg?: number;
  periods: Period[];
  transitioningToIndex: number | null;
  isLabelVisible: boolean;
  setDotRef: (index: number, el: HTMLButtonElement | null) => void;
}

export const Dial = forwardRef<HTMLDivElement, DialProps>(
  (
    {
      total,
      activeIndex,
      groupRotation,
      onDotClick,
      isAnimating = false,
      anchorDeg = -60,
      periods,
      transitioningToIndex,
      isLabelVisible,
      setDotRef,
    },
    ref
  ) => {
    const angles = useMemo(
      () => Array.from({ length: total }, (_, i) => (360 / total) * i),
      [total]
    );

    const expandedIndex = transitioningToIndex ?? activeIndex;

    return (
      <div
        className={styles.dialWrap}
        aria-hidden="true"
        style={{
          ["--anchor" as any]: `${anchorDeg}deg`,
        }}
      >
        <div className={styles.dialCircle} />
        <div className={styles.dialVWhite} />

        <div ref={ref} className={styles.dotsContainer}>
          {angles.map((angle, i) => {
            const isExpanded = expandedIndex === i;
            const period = periods[i];

            return (
              <button
                key={i}
                ref={(el) => setDotRef(i, el)}
                className={[
                  styles.dialDot,
                  isExpanded ? styles.expanded : "",
                  isAnimating ? styles.disabled : "",
                ].join(" ")}
                style={{ ["--a" as any]: `${angle}deg` }}
                onClick={() => !isAnimating && onDotClick(i)}
                type="button"
                aria-label={`Period ${i + 1}`}
              >
                <span
                  className={styles.content}
                  style={{ ["--counterRot" as any]: `${-groupRotation}deg` }}
                >
                  <span className={styles.dotCore} />

                  <span className={styles.dotBubble}>
                    <span className={styles.dotNum}>{period.id}</span>
                  </span>

                  <span
                    className={[
                      styles.dotLabel,
                      isLabelVisible ? "" : styles.labelHidden,
                    ].join(" ")}
                  >
                    {period.title}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
);

Dial.displayName = "Dial";

